import { IntelTechnologiesSortBy, SortDirection } from '@@types/api.types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORTING, deriveSort } from './intel-technologies-sort.util';

type IntelTechnologiesQueryParams = {
  categoryIds?: string[];
  challengeId?: string;
  challengePhaseId?: string;
  companyIds?: string[];
  domainIds?: string[];
  limit: number;
  mrlMax?: number;
  mrlMin?: number;
  page: number;
  search?: string;
  sortBy?: IntelTechnologiesSortBy;
  sortDirection?: SortDirection;
  subcategoryIds?: string[];
  trlMax?: number;
  trlMin?: number;
  useIds?: string[];
};

type UseIntelTechnologiesQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  limit: number;
  page: number;
  phaseFilter: string | null;
  queryParams: IntelTechnologiesQueryParams;
  search: string | null;
  setPage: (page: number) => Promise<URLSearchParams>;
  setPhaseFilter: (phaseFilter: string | null) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState | null) => Promise<URLSearchParams>;
  sortingState: SortingState;
};

export const useIntelTechnologiesQueryParams = (): UseIntelTechnologiesQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));
  const [phaseFilter, setPhaseFilter] = useQueryState('phaseFilter', parseAsString);

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const queryParams = useMemo(() => {
    const { sortBy, sortDirection } = deriveSort(sortingState);

    const params: IntelTechnologiesQueryParams = {
      limit: limit ?? DEFAULT_PAGE_SIZE,
      page: page ?? 1,
      search: search ?? undefined,
      sortBy,
      sortDirection,
    };

    if (phaseFilter && phaseFilter !== 'all') {
      params.challengePhaseId = phaseFilter;
    }

    return params;
  }, [limit, page, phaseFilter, search, sortingState]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    handleLimitChange,
    limit: limit ?? DEFAULT_PAGE_SIZE,
    page: page ?? 1,
    phaseFilter,
    queryParams,
    search,
    setPage,
    setPhaseFilter,
    setSearch,
    setSorting,
    sortingState,
  };
};
